import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ClipboardDocumentListIcon,
  UsersIcon,
  PlusIcon,
  AcademicCapIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";

import { generateAccessCode } from "../utils/helpers";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import EvaluationCard from "../components/EvaluationCard";
import { apiService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const ClubDashboard = () => {
  const { user } = useAuth();
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState({
    totalPlayers: 0,
    totalStaff: 0,
    activeEvaluations: 0,
    completedEvaluations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [evalLimitInfo, setEvalLimitInfo] = useState(null); // { current, limit }

  const loadDashboardData = useCallback(async () => {
    try {
      const [clubData, evaluationsData, statsData] = await Promise.all([
        apiService.getClub(clubId),
        apiService.getClubEvaluations(clubId),
        apiService.getClubStats(clubId),
      ]);

      setClub(clubData);
      setEvaluations(evaluationsData);
      setStats(statsData);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      toast.error("Failed to load dashboard data");
    }
    setLoading(false);
  }, [clubId]);

  useEffect(() => {
    apiService.getClubSubscriptionLimits(clubId)
      .then((limits) => setEvalLimitInfo({ current: limits.evaluations.current, limit: limits.evaluations.limit }))
      .catch((err) => console.warn("Could not load subscription limits:", err));
  }, [clubId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const evalAtLimit = evalLimitInfo?.limit != null && evalLimitInfo.current >= evalLimitInfo.limit;
  const evalLimitTooltip = evalAtLimit
    ? `Your plan allows ${evalLimitInfo.limit} evaluation${evalLimitInfo.limit === 1 ? "" : "s"} per year. Upgrade your plan to create more.`
    : undefined;

  const handleCreateEvaluation = async () => {
    if (evalAtLimit) return;
    try {
      const newEval = {
        name: `Evaluation ${evaluations.length + 1}`,
        clubId,
        status: "draft",
        accessCode: generateAccessCode(),
        assignment_mode: "open",
      };

      await apiService.createEvaluation(newEval);
      await loadDashboardData();
      toast.success("Evaluation created successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to create evaluation");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{club?.name}</h1>
          <p className="text-gray-600">{club?.location}</p>
          <div className="flex items-center space-x-2 mt-2">
            <Badge variant="success">Active</Badge>
            <span className="text-sm text-gray-500">
              Access Code:{" "}
              <span className="font-mono font-semibold">
                {club?.access_code}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-semibold text-sm">
                    ✓
                  </span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.completedEvaluations}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      {user && (user.role === "club_admin" || user.role === "super_admin") && (
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative group">
                <button
                  onClick={handleCreateEvaluation}
                  disabled={evalAtLimit}
                  className={`p-4 border border-gray-200 rounded-lg transition-all text-left w-full ${evalAtLimit ? "opacity-50 cursor-not-allowed" : "hover:border-slate-300 hover:shadow-md"}`}
                >
                  <PlusIcon className="h-8 w-8 text-green-600 mb-2" />
                  <h3 className="font-medium text-gray-900">New Evaluation</h3>
                  <p className="text-sm text-gray-600">
                    Create a new evaluation session
                  </p>
                </button>
                {evalAtLimit && (
                  <div className="absolute bottom-full left-0 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                    {evalLimitTooltip}
                  </div>
                )}
              </div>

              <Link
                to={`/portal/club/${clubId}/staff`}
                className="p-4 border border-gray-200 rounded-lg hover:border-slate-300 hover:shadow-md transition-all"
              >
                <UsersIcon className="h-8 w-8 text-purple-600 mb-2" />
                <h3 className="font-medium text-gray-900">Manage Staff</h3>
                <p className="text-sm text-gray-600">
                  Invite coaches and evaluators
                </p>
              </Link>
              {club?.evaluation_type === "skills" && (
                <Link
                  to={`/portal/club/${clubId}/skills`}
                  className="p-4 border border-gray-200 rounded-lg hover:border-slate-300 hover:shadow-md transition-all"
                >
                  <AcademicCapIcon className="h-8 w-8 text-purple-600 mb-2" />
                  <h3 className="font-medium text-gray-900">Manage Skills</h3>
                  <p className="text-sm text-gray-600">
                    Create and manage skills for athlete evaluations
                  </p>
                </Link>
              )}
              {club?.evaluation_type === "groups" && (
                <Link
                  to={`/portal/club/${clubId}/groups`}
                  className="p-4 border border-gray-200 rounded-lg hover:border-slate-300 hover:shadow-md transition-all"
                >
                  <RectangleGroupIcon className="h-8 w-8 text-purple-600 mb-2" />
                  <h3 className="font-medium text-gray-900">Manage Groups</h3>
                  <p className="text-sm text-gray-600">
                    Create and manage groups for athlete evaluations
                  </p>
                </Link>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Recent Evaluations */}
      <Card>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Evaluations ({evaluations.length})
            </h2>
            {user &&
              (user.role === "club_admin" || user.role === "super_admin") && (
                <div className="relative group">
                  <Button size="sm" onClick={evalAtLimit ? undefined : handleCreateEvaluation} disabled={evalAtLimit}>
                    Create New
                  </Button>
                  {evalAtLimit && (
                    <div className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                      {evalLimitTooltip}
                    </div>
                  )}
                </div>
              )}
          </div>

          {evaluations.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No evaluations
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first evaluation.
              </p>
              <div className="mt-6 relative group inline-block">
                <Button onClick={evalAtLimit ? undefined : handleCreateEvaluation} disabled={evalAtLimit}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  New Evaluation
                </Button>
                {evalAtLimit && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10">
                    {evalLimitTooltip}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {evaluations.map((evaluation) => (
                <EvaluationCard
                  key={evaluation.id}
                  evaluation={evaluation}
                  onUpdate={loadDashboardData}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ClubDashboard;
